import React from "react";
import { QuickStartTask } from "common/constants";
import type { PopoverOrigin } from "@material-ui/core/Popover";

export interface PopoverConfig {
    position: (boundingBox: { top: number; left: number; width: number; height: number }) => { top: number; left: number };
    transformOrigin: PopoverOrigin;
}

export interface PopoverContent {
    title: string;
    body: (onCallback?: () => void) => React.ReactNode | string;
}

export interface TaskConfig {
    title: string;
    subtitle?: string;
    highlightSelector?: string | string[];
    popoverConfig?: PopoverConfig;
    popoverContent?: PopoverContent;
    videoUrl?: string;
    showInQuickStartMenu?: boolean;
}

export const TASK_CONFIGS: Record<string, TaskConfig> = {
    [QuickStartTask.DISCOVER_SMART_SLIDES]: {
        title: "Discover Smart Slides",
        subtitle: "Learn how Smart Slides transform the way you build presentations.",
        videoUrl: "http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4",
        showInQuickStartMenu: true
    },
    [QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE]: {
        title: "Add your first Smart Slide",
        highlightSelector: "#add-slide-button",
        popoverContent: {
            title: "Welcome to the Smart Slide library",
            body: onCallback => (
                <span>Click on any slide to add it to your presentation. Not sure where to start? Try the <b
                    style={{ cursor: "pointer" }}
                    onClick={onCallback}
                >Radial Bar Chart</b> slide—it's one of our favorites.</span>
            )
        },
        showInQuickStartMenu: true
    },
    [QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE_END]: {
        title: "Add your first Smart Slide",
        popoverContent: {
            title: "There are endless ways to customize a Smart Slide",
            body: () => (
                <span>The buttons below each slide let you add content. You can replace placeholders with your own text, images, icons, logos, videos, and more.</span>
            )
        },
        showInQuickStartMenu: false
    },
    [QuickStartTask.DIVE_DEEPER_WITH_THE_PANEL]: {
        title: "Dive deeper with the panel",
        highlightSelector: [".activeSidePanel", "#side-menu-bar"],
        popoverConfig: {
            position: box => ({ top: box.top, left: box.left }),
            transformOrigin: { vertical: "top", horizontal: "right" }
        },
        popoverContent: {
            title: "For everything else, there's the panel",
            body: () => (
                <span>Look right 👉 to see all your slide options. Change colors, tweak your layout, see edit options, or browse slide ideas to switch your Smart Slide completely. </span>
            )
        },
        showInQuickStartMenu: true
    },
    [QuickStartTask.PERSONALIZE_YOUR_THEME]: {
        title: "Personalize your theme",
        highlightSelector: "#theme-menu",
        popoverConfig: {
            position: box => ({ top: box.top + box.height / 2, left: box.left + box.width }),
            transformOrigin: { vertical: "center", horizontal: "left" }
        },
        popoverContent: {
            title: "Personalize it with a theme",
            body: () => (
                <span>Pick a built-in theme or craft your own. Refine colors, fonts, image styles, and every detail in between.</span>
            )
        },
        showInQuickStartMenu: true
    },
    [QuickStartTask.PRESENT_LIKE_A_PRO]: {
        title: "Present and share",
        highlightSelector: ["#present-presentation", "#share-presentation"],
        popoverConfig: {
            position: box => ({ top: box.top + box.height, left: box.left }),
            transformOrigin: { vertical: "bottom", horizontal: "right" }
        },
        popoverContent: {
            title: "Show it off",
            body: () => (
                <span>When you <b>Present</b>, Smart Slides animate automatically in the Player. Use <b>Share</b> to collaborate or create a public link.</span>
            )
        },
        showInQuickStartMenu: true
    }
};

